import React from "react";
import { HashLink } from "react-router-hash-link";
import { Boxes, Users, ArrowRight } from "lucide-react";
import Reveal from "../common/Reveal";

/**
 * Two-column split built from EMIBOOK's own home screen — Orders and
 * Customers are the app's two primary tiles, not invented personas.
 */
const PANELS = [
  {
    key: "orders",
    icon: Boxes,
    chip: "On the home screen",
    title: "Orders",
    body: "Every loan issued, every EMI schedule and every payment collected — one running order you can search in seconds.",
    accent: "#2563eb",
    accentSoft: "rgba(37, 99, 235, 0.09)",
  },
  {
    key: "customers",
    icon: Users,
    chip: "On the home screen",
    title: "Customers",
    body: "Contact details, KYC and the loans tied to each borrower, kept in one directory instead of a shelf of registers.",
    accent: "#4f46e5",
    accentSoft: "rgba(79, 70, 229, 0.09)",
  },
];

function DottedRing({ style, className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        position: "absolute",
        border: "1.5px dashed currentColor",
        borderRadius: "50%",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}

export default function AudienceSplit() {
  return (
    <section className="eb-bg-white eb-section">
      <div className="container">
        <Reveal className="row" style={{ marginBottom: "clamp(36px, 5vw, 56px)" }}>
          <div className="col-lg-7">
            <span className="eb-eyebrow">Built around your workflow</span>
            <h2 className="eb-section-title">
              Two tiles. Everything else follows.
            </h2>
          </div>
        </Reveal>

        <div className="row g-3 g-lg-4">
          {PANELS.map((panel, idx) => {
            const Icon = panel.icon;
            return (
              <Reveal className="col-lg-6" delay={idx * 0.08} key={panel.key}>
                <div
                  className="eb-split-panel"
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "var(--radius-lg)",
                    border: "1px solid var(--border-default)",
                    background: `linear-gradient(165deg, ${panel.accentSoft} 0%, #ffffff 58%)`,
                    padding: "clamp(30px, 3.6vw, 46px)",
                    minHeight: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    color: panel.accent,
                  }}
                >
                  <DottedRing
                    className="eb-split-ring"
                    style={{ width: "220px", height: "220px", top: "-70px", right: "-70px" }}
                  />
                  <DottedRing
                    className="eb-split-ring"
                    style={{ width: "130px", height: "130px", bottom: "-40px", left: "-40px" }}
                  />
                  <DottedRing
                    className="eb-split-ring"
                    style={{ width: "70px", height: "70px", bottom: "30%", right: "12%" }}
                  />

                  <div style={{ position: "relative", zIndex: 1 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "5px 12px",
                        borderRadius: "9999px",
                        background: "#ffffff",
                        border: `1px solid ${panel.accent}30`,
                        fontFamily: "var(--font-display)",
                        fontSize: "0.74rem",
                        fontWeight: 600,
                        color: panel.accent,
                        marginBottom: "22px",
                      }}
                    >
                      <Icon size={13} />
                      {panel.chip}
                    </span>

                    <h3
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "clamp(1.9rem, 3vw, 2.5rem)",
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        color: "var(--text-main)",
                        margin: "0 0 12px",
                      }}
                    >
                      {panel.title}
                    </h3>

                    <p
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.98rem",
                        lineHeight: 1.65,
                        maxWidth: "380px",
                        margin: 0,
                      }}
                    >
                      {panel.body}
                    </p>
                  </div>

                  <div style={{ position: "relative", zIndex: 1, marginTop: "28px" }}>
                    <HashLink
                      smooth
                      to="#how-it-works"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        fontFamily: "var(--font-display)",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: panel.accent,
                        textDecoration: "none",
                      }}
                    >
                      See how it works <ArrowRight size={15} />
                    </HashLink>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
